import { useRef, type ReactElement } from "react";
import { Link } from "react-router";

import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { useSearchForm } from "../../forms/use-search-form.js";

import { TenantInvite } from "./tenant-invite.js";
import type { CenterTenant } from "./tenant-types.js";
import { UserPager } from "./tenant-user-pager.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

/**
 * 最後の確認はブラウザの確認ダイアログで行う（重要な操作は最後にダイアログで確かめる。2026-10-10 に利用者が決めた）。
 * ロックの解除の画面と、契約 1 件の画面の利用者の一覧で同じ文。
 */
export const unlockDialog = (email: string): string =>
    `「${email}」のロックを解除します。連続失敗の回数も 0 に戻ります。よろしいですか？`;

/**
 * ロックを解除するボタン。<b>口は解除の画面と同じ 1 つ</b>（判定と文言を 2 通りにしない）。
 * 契約 ID を添えて送ると、終わった後この契約の画面へ戻る。
 */
export function UnlockButton({ tenantId, email, className }: {
    readonly tenantId: number;
    readonly email: string;
    readonly className: string;
}): ReactElement {
    const form = useApiForm(adminCenterApi.unlock, undefined, unlockDialog(email));

    return (
        <form method="post" onSubmit={form.onSubmit}>
            <FormSummary messages={form.summary} />
            <input type="hidden" name="id" value={tenantId} />
            <input type="hidden" name="email" value={email} />
            <button type="submit" className={className} disabled={form.busy}>ロックを解除</button>
        </form>
    );
}

/**
 * ロック中の札と、いつからいつまでか。<b>解除の画面と同じ見せ方</b>（目立つ赤の札）。
 * 時刻は API が日本時間の文字にしたものを並べるだけ。
 */
export function LockedBadge({ since, until }: {
    readonly since: string | null;
    readonly until: string | null;
}): ReactElement {
    return (
        <>
            <span className="badge-danger">ロック中</span>
            {since === null || until === null
                ? null
                : <span className="ml-2 text-xs text-slate-500">{`${since} から（${until} まで）`}</span>}
        </>
    );
}

/**
 * 利用者を絞り込む（語と状態）。<b>並び順は絞り込んでも保つ。</b>ページは載せない——絞り込みを変えたら 1 ページ目に戻す。
 */
function UserFilter({ tenant }: { readonly tenant: CenterTenant }): ReactElement {
    const status = tenant.userStatus === null ? "" : String(tenant.userStatus);
    const form = useSearchForm(adminCenterUrls.tenant(tenant.tenantId), { userKeyword: tenant.userKeyword ?? "", userStatus: status });

    return (
        // 1 テナントに最大 2,059 名居る。**絞り込めないと辿り着けない**
        <form method="get" {...form.props} className="mb-4 flex flex-wrap items-end gap-2">
            <input type="hidden" name="userSort" value={tenant.userSort} />
            <div className="min-w-0 grow">
                <label className="form-label" htmlFor="userKeyword">利用者を絞り込む</label>
                <input
                    className="form-input" id="userKeyword" name="userKeyword"
                    defaultValue={tenant.userKeyword ?? ""} autoComplete="off"
                    placeholder="メールアドレス・表示名"
                />
            </div>
            <div>
                <label className="form-label" htmlFor="userStatus">状態</label>
                <select className="form-input" id="userStatus" name="userStatus" defaultValue={status}>
                    <option value="">すべて</option>
                    {tenant.userStatusChoices.map((o) => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                </select>
            </div>
            {/* 送るボタンとクリアはひとまとまりにする（折り返すときも一緒に次の行へ落ちる。片方だけ別の行に分かれない） */}
            <div className="flex gap-2">
                <button type="submit" className="btn-primary">絞り込む</button>
                {/* **クリアは「条件の無い URL を開く」。**type="reset" は描いたときの値に戻るだけ */}
                <Link to={adminCenterUrls.tenant(tenant.tenantId)} className="btn-secondary">クリア</Link>
            </div>
        </form>
    );
}

/** 利用者の一覧とロックの解除・利用者の招待・契約者の変更の入口。<b>アカウント管理を持つ人だけ</b>（サーバが canUnlock・canInvite・canChangeContractor で知らせる）。 */
export function TenantUsers({ tenant }: { readonly tenant: CenterTenant }): ReactElement {
    const listTop = useRef<HTMLDivElement | null>(null);

    return (
        <section className="card mb-4 p-6">
            <h2 className="section-title">利用者（{tenant.matchingUserCount}）</h2>
            <p className="mb-4 text-sm text-slate-500">
                ロックはポータルとプリザンター（<code>/fs</code>）で共有されます。
            </p>

            {/* 利用者の招待。新しい契約者にする方がまだ契約に居ないとき。出すかはサーバが決める（アカウント管理・解約していない契約） */}
            {tenant.canInvite ? <TenantInvite tenantId={tenant.tenantId} title={tenant.title} /> : null}

            {/* 契約者の変更（強制）。入口を出すかはサーバが決める（アカウント管理・解約していない契約） */}
            {tenant.canChangeContractor
                ? (
                    <div className="mb-4 flex items-center justify-between gap-4" data-change-contractor="">
                        <p className="text-sm text-slate-500">契約者が居なくなって引き継げないときは、運営が契約者を替えられます。</p>
                        <Link to={adminCenterUrls.contractor(tenant.tenantId)} className="btn-secondary btn-sm shrink-0">契約者を変更する</Link>
                    </div>
                )
                : null}

            <UserFilter tenant={tenant} />

            <div className="table-card" ref={listTop}>
                <div className="table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                {tenant.userColumns.map((column) => (
                                    <th scope="col" key={column.label}>
                                        {/* 並べ替えても絞り込みは残し、1 ページ目に戻す（ページは載せない） */}
                                        <Link to={adminCenterUrls.tenant(tenant.tenantId, {
                                            userKeyword: tenant.userKeyword, userStatus: tenant.userStatus, userSort: column.next,
                                        })}
                                        >
                                            {column.label}
                                        </Link>
                                        <span aria-hidden="true">{column.marker}</span>
                                    </th>
                                ))}
                                <th scope="col">ロック</th>
                                <th scope="col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenant.users.map((user) => (
                                <tr key={user.email}>
                                    <td className="font-medium text-ink-800">{user.email}</td>
                                    <td>{user.userName}</td>
                                    <td>
                                        {user.role}
                                        {user.inviting ? <> <span className="badge-info">招待中</span></> : null}
                                    </td>
                                    <td>
                                        {user.isLockedOut ? <LockedBadge since={user.lockedSince} until={user.lockedUntil} /> : null}
                                    </td>
                                    <td className="cell-actions">
                                        {/* 解除はロック中の人にだけ（ロックされていない人に押せても何も起きない） */}
                                        {tenant.canUnlock && user.isLockedOut && !user.inviting
                                            ? <UnlockButton tenantId={tenant.tenantId} email={user.email} className="btn-secondary btn-sm" />
                                            : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {tenant.users.length === 0
                ? (
                    <div className="empty-state mt-4 text-sm text-slate-500">
                        {tenant.usersNarrowed ? "当たる利用者がいませんでした。" : "利用者がいません。"}
                    </div>
                )
                : null}

            <UserPager tenant={tenant} top={listTop} />
        </section>
    );
}
